/**
 * The change log: who edited a brand's FAQ, when, and what it said before.
 *
 * Not to be confused with `faq.service.js`'s `audit()`, which grades the FAQ's
 * QUALITY and says nothing about who wrote it. Same English word, two
 * unrelated questions — the paths keep them apart (`/changes` vs `/faq/audit`)
 * and so do the screens.
 *
 * Read-only: there is no save, no delete and no edit here, and there is no
 * endpoint behind one either. The rows are written by ai_bot as a side effect
 * of the change itself.
 *
 * Service-direct rather than through Vuex: the rows come from ai_bot, not this
 * app's database, so the listing store's pagination adapter (which speaks
 * Laravel's paginator shape) does not fit. The page keeps its own
 * offset/limit, which is what ai_bot actually returns.
 */
import { apiHelper } from '@/utils'
import { API } from '@/constants'

export const auditLogService = {
  list,
  get,
  actors,
}

const API_URL = API.BASE_URL + '/v1'

/**
 * Scope the call to the tenant named in the path, not to whatever the brand
 * picker last selected. Same reason as faq.service.js — and still authorised
 * server-side by AuditLogController::guard, so naming a brand here cannot
 * reach one this user does not have.
 */
function forTenant(tenantId) {
  return { headers: { 'X-Tenant-Id': String(tenantId) } }
}

/**
 * Newest first. `filters` may carry entity, entity_id, actor, action, since,
 * until, limit, offset — every one optional.
 *
 * Values come back PREVIEWED (first 200 characters). The whole before/after is
 * `get()`, on the one row the reader opened.
 */
export async function list(tenantId, filters = {}) {
  const params = {}
  for (const [k, v] of Object.entries(filters)) {
    // An empty string is "no filter", not "match the empty value" — sending it
    // would ask ai_bot for rows whose actor is literally ''.
    if (v !== null && v !== undefined && v !== '') params[k] = v
  }
  return apiHelper(API_URL).get(`/tenants/${tenantId}/changes`, {
    ...forTenant(tenantId),
    params,
  })
}

/** One change with the whole of what it was and what it became. */
export async function get(tenantId, entryId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/changes/${entryId}`, forTenant(tenantId))
}

/** The distinct people who have changed anything, for the filter dropdown. */
export async function actors(tenantId) {
  return apiHelper(API_URL).get(`/tenants/${tenantId}/changes/actors`, forTenant(tenantId))
}
